import * as Mutations from "./mutation-types";
import * as Actions from "./action-types";
import * as api from "@/api/api";
import * as utils from "@/api/utils";

import * as Helpers from "@/helpers";
import { executeQueryHelper } from "@/store/CommonStoreHelpers";
import _ from "lodash";

const defaultCampaignsQuery = {
  start_date: "",
  end_date: "",
  channels: {},
  buyingAgencies: null,
  advertisers: null,
  products: null,
  film_code: [],
  groupby: null,
  target_group: []
};

const defaultCampaignsRoFQuery = {
  keys: [],
  return_type: "absolute",
  aggregation_type: "cumulative",
  reachCritera: "0",
  max_frequencies: 10,
  displayColumns: [],
};

const state = {
  hasSetDefaultCampaignsQuery: false,
  queryCost: {
    data: null,
    isLoading: false,
    error: null
  },
  campaignsInitStatus: "NOT_LOADED",
  campaignsQuery: _.cloneDeep(defaultCampaignsQuery),  
  defaultCampaignsQuery: _.cloneDeep(defaultCampaignsQuery),
  campaignsRoFQuery: {
    ...defaultCampaignsRoFQuery
  },
  campaignsQueryForAPI: {},
  campaignsResults: {
    status: "NOT_LOADED"
  },
  campaignsRoFResults: {
    status: "NOT_LOADED"
  },
  campaignsRoFResultsLoadDetails: false,
  campaignsSummary: [],
  campaignsColumnTypes: [],
  campaignsDisplayColumns: [],
  campaignsTimeshift: [],
  campaignsColumnFieldsOptions: [],
  campaignsDisplayColumnFieldsOptions: [],
  campaignsAllWithStarSelected: true,
  campaignsSumVisible: false,
  campaignsMeasures: [],
  campaignsExcludedCampaigns: [],
  campaignsQueryBeforeChange: {},
  campaignsStringForFilterBeforeChange: ""
};

const convertFromQueryToQueryForAPI = (
  ctx,
  q,
  key_column,
  options = {
    sortBy: null,
    sort_direction: "ASC",
    rows_per_page: 10000000
  }
) => {

  // transformera interna queryObjektet till ett som passar vår API
  const defaultCampaignsQueryForAPI = {
    spot_types: q.spot_types,
    product_class: q.product_class,
    rows_per_page: options.rows_per_page,
    film_code: q.film_code,
    sort_by: options.sortBy,
    sort_direction: options.sort_direction,
    columns: [],
    displayColumns: [],
    measures: [],
    min_slot_length: 0,
    filters: null,
    orig_codes: q.channels
      ? Object.keys(q.channels).reduce(
          (prev, key) => [...prev, ...q.channels[key]],
          []
        )
      : [],
    key_column: {
      column_type: key_column.column_type,
      display_name: key_column.display_name
    },
    last_row_values: null,
    search: null,
    tab: "reklam",
    target_group: [],
  };

  const defaultMeasure = {
    min_viewing_time: 0
  };
  const defaultColumn = {};

  // är inga groupbycolumns valda så skickar vi med alla kolumner
  var columns = q.columns;
  if (columns.length == 0) {
    columns = ctx.state.campaignsColumnTypes.filter(x => x.hidden == false);
  }

  // + skicka alltid med alwaysIncludeInGroupBy, om de inte redan finns
  columns = columns.concat(ctx.state.campaignsColumnTypes
    .filter(col => col.alwaysIncludeInGroupBy && !columns.some(c => c.column_type === col.column_type)));

  columns = columns.map(x => ({ column_type: x.column_type, display_name: x.display_name }));


  const APIQuery = {
    ...defaultCampaignsQueryForAPI,
    start_date: q.start_date ?? null,
    end_date: q.end_date ?? null,
    buyingAgency: q.buyingAgencies,
    advertiser: q.advertisers,
    product: q.products,
    min_slot_length: q.duration ? q.duration * 60 : 0,
    measures: q.target_group
      ? q.target_group.map(measure => {
          let targetGroup = {
            group: measure.group == 'W' ? 'K' : measure.group,
            age_from: measure.age_from,
            age_to: measure.age_to
          };
          
          // Add Other target group fields if group is "O"
          if (measure.group === 'O' && measure.othertargetgroups && measure.othertargetgroupsvalues) {
            targetGroup.other_category = measure.othertargetgroups;
            targetGroup.other_value = measure.othertargetgroupsvalues;
          }
          
          return {
            ...defaultMeasure,
            min_viewing_time: measure.reach_criteria,
            target_group: targetGroup,
            consolidated: measure.timeshift.consolidated,
            days: measure.timeshift.days,
            timeshift: measure.timeshift.keys,
            display_name: Helpers.createMeasureName(measure),
            column_type: measure.measure.column_type
          };
        })
      : [],
    columns: columns
      ? columns.map(column => ({
        ...defaultColumn,
        ...column
      }))
      : [],
    displayColumns: q.displayColumns
      ? q.displayColumns.map(column => ({
          ...defaultColumn,
          ...column
        }))
      : []
  };

  return APIQuery;
};

const executeRoFQuery = async (context, query) => {
  try {
    var r = await api.executeCampaignsRoFQuery(query);
    // fått ett svar

    // kolla om vi fått några rader
    if (!(r.keys && r.keys.length > 0)) {
      context.commit(Mutations.setCampaignsRoFResults, {
        status: "EMPTY_RESULT"
      });
    } else {
      // spara resultaten formaterat enligt query

      const formatFunc = val =>
        query.return_type == "absolute" // absoluta värden visas i tusental och avrundat
          ? Math.round(val / 1000, 0).toLocaleString("sv-Sv") // relativa värden visas i procen med 1 decimal
          : `${Math.round(val * 1000) / 10}`;

      context.commit(Mutations.setCampaignsRoFResults, {
        status: "LOADED",
        data: {
          ...r,
          // formatera frekvensmatrisen
          frequency_matrix: r.frequency_matrix.map(row =>
            row.map(cell => formatFunc(cell))
          ),
          reach_1_plus_acc: r.reach_1_plus_acc.map(cell => formatFunc(cell)),
          average_frquency: r.average_frquency.map(cell => cell.toFixed(1).replace('.', ',')),
          rating: r.rating.map(cell => formatFunc(cell))
        }
      });
    }
  } catch (error) {
    context.commit(Mutations.setCampaignsRoFResults, {
      status: "FAILED",
      data: null,
      error: error
    });
  }
};

const executeQuery = async (context, query) => {
  await executeQueryHelper(context, query, {
    moduleName: "Campaigns",
    mutations: Mutations,
    getters: context.getters,
    executeQueryFn: api.executeQueryDataQuery,
    processRowFn: (row) => ({
      ...row,
      Produkt: row.Produkt ? row.Produkt.toUpperCase() : "",
      Annonsör: row.Annonsör ? row.Annonsör.toUpperCase() : "",
      Byrå: row.Byrå ? row.Byrå.toUpperCase() : "",
      selected: false
    })
  });
};

const GetRoFQuery = context => {
  // konvertera till fråga för API
  const keycolumn = context.state.campaignsColumnTypes.filter(
    col => col.key
  )[0];
  const query = {
    keys: context.state.campaignsRoFQuery.keys.map(key => [
      {
        DAXIdentifier: keycolumn.column_type,
        Value: `${key}`
      }
    ]),
    panel_date: context.state.campaignsRoFQuery.panel_date,
    reach_criteria: context.state.campaignsRoFQuery.reachCriteria,
    return_type: context.state.campaignsRoFQuery.return_type,
    aggregation_type: context.state.campaignsRoFQuery.aggregation_type,
    max_frequencies: context.state.campaignsRoFQuery.max_frequencies,
    target_group: context.state.campaignsRoFQuery.target_group_rof,
    start_date: context.state.campaignsRoFQuery.start_date,
    end_date: context.state.campaignsRoFQuery.end_date
  };
  if(query.target_group.group === 'W') 
    query.target_group.group = 'K';
  return query;
};

const PrepareQuery = async context => {
  const selColumns = context.getters["getCampaignsQuery"].columns;
  const columnFieldsOptions =
    context.getters["getCampaignsColumnFieldsOptions"];

  const allStarSelected = Helpers.allWithStarsSelected(
    selColumns,
    columnFieldsOptions
  );

  context.commit(Mutations.setCampaignsAllWithStarSelected, allStarSelected);

  // konvertera till fråga för API
  // TODO - OM man inte valt alla * i kolumnväljaren; så ska man skicka tom keycolumn (som överst i exemplet)
  let keycolumn = null;

  if (allStarSelected) {
    keycolumn = context.state.campaignsColumnTypes.filter(col => col.key)[0];
  } else {
    keycolumn = {
      column_type: null,
      display_name: null
    };
  }

  // konvertera till fråga för API
  const columnsWithoutKey = context.state.campaignsColumnTypes
    .filter(col => !col.key)
    .map(x => x.display_name);
  context.state.campaignsQuery.columns.sort(function(a, b) {
    return (
      columnsWithoutKey.indexOf(a.display_name) -
      columnsWithoutKey.indexOf(b.display_name)
    );
  });
  const query = convertFromQueryToQueryForAPI(
    context,
    context.state.campaignsQuery,
    keycolumn
  );

  // comitta queryn till store (så att den används vid exporten)
  await context.commit(Mutations.setCampaignsQueryForAPI, query);
};

const actions = {
  // hämta alla data som krävs för att visa sidan

  [Actions.getCampaignsColumnTypes]: async () => {
    return await api.getColumnTypes("SPOTS");
  },
  [Actions.getCampaignsMeasures]: async () => {
    return await api.getMeasures("SPOTS");
  },
  [Actions.getCampaignsChannels]: async () => {
    let dateQuery = {
      start_date: state.campaignsQuery.start_date,
      end_date: state.campaignsQuery.end_date
    };

    return await api.getAllChannels(dateQuery);
  },
  [Actions.getCampaignsBuyingAgencies]: async () => {
    let dateQuery = {
      StartDate: state.campaignsQuery.start_date,
      EndDate: state.campaignsQuery.end_date
    };
    return await api.getBuyingAgencies(dateQuery);
  },
  [Actions.getCampaignsAdvertisers]: async () => {
    let dateQuery = {
      StartDate: state.campaignsQuery.start_date,
      EndDate: state.campaignsQuery.end_date
    };

    return await api.getAdvertisers(dateQuery);
  },
  [Actions.getCampaignsProducts]: async () => {
    let dateQuery = {
      StartDate: state.campaignsQuery.start_date,
      EndDate: state.campaignsQuery.end_date
    };

    return await api.getProducts(dateQuery);
  },
  [Actions.getCampaignsTimeshift]: async () => {
    const timeshift = await api.getTimeshift();
    // här tar vi just nu bara de för aggregerade timeshifts,
    // alltså KO, OV, LIVE osv. och grupperar ihop

    return timeshift;
  },
  [Actions.getCampaignsProductClasses]: async () => {
    const productClasses = await api.getProductClasses();
    return productClasses;
  },
  // denna initialiserar tabellen och frågan, alltså använda då tryck på 'Sök'
  [Actions.executeCampaignsQuery]: async context => {
    context.commit(Mutations.setCampaignsResults, {
      status: "LOADING",
      data: null
    });

    // initiera/resetta selected rows
    context.commit(Mutations.setCampaignsExcludedCampaigns, []);

    await PrepareQuery(context);

    const query = context.state.campaignsQueryForAPI;

    // kolla om frågan är OK att köra och returnera till gränssnittet, annars sätt queryCost så tar vi det därifrån
    context.commit(Mutations.setQueryCostLoading, true);
    context.commit(Mutations.setQueryCostError, null);

    try {
      const queryCostData = await api.getQueryCost(query);
      await context.commit(Mutations.setQueryCostData, queryCostData);
    } catch (error) {
      context.commit(Mutations.setCampaignsResults, {
        status: "VALIDATION_FAILED"
      });
      await context.commit(Mutations.setQueryCostError, error.response.data.message);
      return;
    } finally {
      await context.commit(Mutations.setQueryCostLoading, false);
    }
    if (
      context.state.queryCost.error == null &&
      context.state.queryCost.data.queryClass == "Normal"
    ) {
      await executeQuery(context, query);
    } else {
      context.commit(Mutations.setCampaignsResults, {
        status: "NOT_LOADING",
        data: null
      });
    }
  },
  [Actions.executeScheduleQuery]: async (context, reportname) => {
    await PrepareQuery(context);

    const query = context.state.campaignsQueryForAPI;

    await api.createScheduledReportQuery(reportname, query);
  },

  [Actions.executeExportCampaignsQuery]: async (
    context,
    showGroupByDropdowns
  ) => {
    const query = context.state.campaignsQueryForAPI;
    // om vi inte renderat datat, så finns inte dessa dropdowns
    let aggregationTypeDropdowns =
      context.getters.getCampaignsResults?.data?.aggregationTypeDropdowns;
    if (aggregationTypeDropdowns != null) {
      query.measures = query.measures.map(measure => ({
        ...measure,
        default_aggregation: showGroupByDropdowns
          ? aggregationTypeDropdowns
              .find(t => t.key === measure.display_name)
              .value.toUpperCase()
          : "AVG"
      }));
    }
    query.sort_by = context.state.campaignsQueryForAPI.sort_by;
    query.sort_direction = context.state.campaignsQueryForAPI.sort_direction;
    query.last_row_values = null;
    query.showGroupByDropdowns = showGroupByDropdowns;
    query.excludedRows = context.state.campaignsExcludedCampaigns;
    return await api.executeExportQuery(query);
  },

  [Actions.executeCampaignsRoFQuery]: async context => {
    context.commit(Mutations.setCampaignsRoFResults, {
      status: "LOADING",
      data: null
    });
    const query = GetRoFQuery(context);
    await executeRoFQuery(context, query);
  },
  [Actions.executeCampaignsQueryMoreExclude]: async (
    context,
    rowsWithoutExcludedRows
  ) => {
    context.commit(Mutations.setCampaignsResults, {
      ...context.state.campaignsResults,
      data: {
        ...context.state.campaignsResults.data,
        rows: rowsWithoutExcludedRows
      },
      loadingTable: false
    });
  },
  [Actions.changeSortCampaigns]: async (context, payload) => {
    let columnInfo = context.getters.getCampaignsResults.data.columns.find(
      c => c.key == payload.sortBy
    );
    let tableRows = [...context.getters.getCampaignsResults.data.rows];
    if (columnInfo.isNumber) {
      if (payload.sortDesc)
        tableRows.sort((a, b) => {
          let first = a[payload.sortBy];
          let second = b[payload.sortBy];
          if (typeof first === "string")
            first = parseFloat(first.replace(/\s/g, "").replace(/,/g, "."));
          if (typeof second === "string")
            second = parseFloat(second.replace(/\s/g, "").replace(/,/g, "."));
          return second - first;
        });
      else {
        tableRows.sort((a, b) => {
          let first = a[payload.sortBy];
          let second = b[payload.sortBy];
          if (typeof first === "string")
            first = parseFloat(first.replace(/\s/g, "").replace(/,/g, "."));
          if (typeof second === "string")
            second = parseFloat(second.replace(/\s/g, "").replace(/,/g, "."));
          return first - second;
        });
      }
    } else if (columnInfo.isPercentage) {
      if (payload.sortDesc) {
        tableRows.sort((a, b) => {
          a = parseFloat(
            a[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          b = parseFloat(
            b[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          if (a < b) {
            return 1;
          }
          if (a > b) {
            return -1;
          }
          return 0;
        });
      } else {
        tableRows.sort((a, b) => {
          a = parseFloat(
            a[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          b = parseFloat(
            b[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          if (a < b) {
            return -1;
          }
          if (a > b) {
            return 1;
          }
          return 0;
        });
      }
    } else if (columnInfo.isWeekday) {
      utils.sortAsWeekday(tableRows, payload);
    } else {
      if (payload.sortDesc) {
        tableRows.sort((a, b) => {
          a = a[payload.sortBy];
          b = b[payload.sortBy];
          if (a < b || a == null) {
            return 1;
          }
          if (a > b || b == null) {
            return -1;
          }
          return 0;
        });
      } else {
        tableRows.sort((a, b) => {
          a = a[payload.sortBy];
          b = b[payload.sortBy];
          if (a < b || a == null) {
            return -1;
          }
          if (a > b || b == null) {
            return 1;
          }
          return 0;
        });
      }
    }
    let summaryArray = context.getters.getCampaignsResults.data.summary.array.map(
      obj => {
        return {
          ...obj,
          visible: false
        };
      }
    );

    context.commit(Mutations.setCampaignsResults, {
      ...state.campaignsResults,
      data: {
        ...context.state.campaignsResults.data,
        rows: tableRows,
        summary: {
          ...context.state.campaignsResults.data.summary,
          array: summaryArray
        }
      },
      loadingTable: false
    });

    const sortDir = payload.sortDesc === true ? "DESC" : "ASC";
    // skapa kolumnobjekt från data-objektet
    const column = context.getters.getCampaignsResults.data.columns.filter(
      c => c.key == payload.sortBy
    )[0];
    const sortByColumn = (({ column_type, key }) => ({
      column_type,
      display_name: key
    }))(column);
    context.commit(Mutations.setCampaignsQueryForAPI, {
      ...context.getters.getCampaignsQueryForAPI,
      sort_by: sortByColumn,
      sort_direction: sortDir
    });
  },

  [Actions.getCampaignsSpottypes]: async () => {
    return await api.getSpottypes();
  }
};

const getters = {
  getHasSetDefaultCampaignsQuery: () => state.hasSetDefaultCampaignsQuery,
  getDefaultCampaignsQuery: () => state.defaultCampaignsQuery,
  getCampaignsQuery: state => state.campaignsQuery,
  getCampaignsRoFQuery: state => state.campaignsRoFQuery,
  getCampaignsResults: state => state.campaignsResults,
  getCampaignsRoFResults: state => state.campaignsRoFResults,
  getCampaignsRoFResultsLoadDetails: state =>
    state.campaignsRoFResultsLoadDetails,
  getCampaignsSummary: state => state.campaignsSummary,
  getCampaignsQueryForAPI: state => state.campaignsQueryForAPI,
  getCampaignsColumnTypes: ({ campaignsColumnTypes }) => campaignsColumnTypes,
  getCampaignsChannels: state => state.campaignsChannels,
  getCampaignsInitStatus: ({ campaignsInitStatus }) => campaignsInitStatus,
  getCampaignsMeasures: state => state.campaignsMeasures,
  getCampaignsSpottypes: state => state.campaignsSpottypes,
  getCampaignsColumnFieldsOptions: state => state.campaignsColumnFieldsOptions,
  getCampaignsAllWithStarSelected: state => state.campaignsAllWithStarSelected,
  getCampaignsSumVisible: state => state.campaignsSumVisible,
  getCampaignsQueryCost: state => state.queryCost,
  getCampaignsExcludedCampaigns: state => state.campaignsExcludedCampaigns,
  getCampaignsQueryBeforeChange: state => state.campaignsQueryBeforeChange,
  getCampaignsStringForFilterBeforeChange: state =>
    state.campaignsStringForFilterBeforeChange
};

const mutations = {
  [Mutations.setHasSetDefaultCampaignsQuery]: (state, payload) =>
    (state.hasSetDefaultCampaignsQuery = payload),
  [Mutations.setDefaultCampaignsQuery]: (state, payload) =>
    (state.defaultCampaignsQuery = payload),
  [Mutations.setCampaignsQuery]: (state, payload) =>
    (state.campaignsQuery = payload),
  [Mutations.setCampaignsRoFQuery]: (state, payload) =>
    (state.campaignsRoFQuery = payload),
  [Mutations.setCampaignsResults]: (state, payload) =>
    (state.campaignsResults = payload),
  [Mutations.setCampaignsResultsColumns]: (state, payload) =>
    (state.campaignsResults.data.columns = payload),
  [Mutations.setCampaignsResultsAggregationTypes]: (state, payload) =>
    (state.campaignsResults.data.aggregationTypeDropdowns = payload),
  [Mutations.setCampaignsSummary]: (state, payload) =>
    (state.campaignsSummary = payload),
  [Mutations.setCampaignsColumnTypes]: (state, payload) =>
    (state.campaignsColumnTypes = payload),
  [Mutations.setCampaignsInitStatus]: (state, payload) =>
    (state.campaignsInitStatus = payload),
  [Mutations.setCampaignsQueryForAPI]: (state, payload) =>
    (state.campaignsQueryForAPI = payload),
  [Mutations.setCampaignsTimeshift]: (state, payload) =>
    (state.campaignsTimeshift = payload),
  [Mutations.setCampaignsChannels]: (state, payload) =>
    (state.campaignsChannels = payload),
  [Mutations.setCampaignsRoFResults]: (state, payload) =>
    (state.campaignsRoFResults = payload),
  [Mutations.setCampaignsRoFResultsLoadDetails]: (state, payload) =>
    (state.campaignsRoFResultsLoadDetails = payload),
  [Mutations.setCampaignsStartdate]: (state, payload) =>
    (state.campaignsQuery.start_date = payload),
  [Mutations.setCampaignsEnddate]: (state, payload) =>
    (state.campaignsQuery.end_date = payload),
  [Mutations.setCampaignsMeasures]: (state, payload) =>
    (state.campaignsMeasures = payload),
  [Mutations.setCampaignsSpottypes]: (state, payload) =>
    (state.campaignsSpottypes = payload),
  [Mutations.setCampaignsColumnFieldsOptions]: (state, payload) =>
    (state.campaignsColumnFieldsOptions = payload),
  [Mutations.setCampaignsAllWithStarSelected]: (state, payload) =>
    (state.campaignsAllWithStarSelected = payload),
  [Mutations.setCampaignsSumVisible]: (state, payload) =>
    (state.campaignsSumVisible = payload),
  [Mutations.setCampaignsGroupBy]: (state, payload) =>
    (state.campaignsQuery.groupby = payload),
  [Mutations.setCampaignsDisplayColumnFieldsOptions]: (state, payload) =>
    (state.campaignsDisplayColumnFieldsOptions = payload),
  [Mutations.setQueryCostData]: (state, payload) =>
    (state.queryCost.data = payload),
  [Mutations.setQueryCostLoading]: (state, payload) =>
    (state.queryCost.loading = payload),
  [Mutations.setQueryCostError]: (state, payload) =>
    (state.queryCost.error = payload),
  [Mutations.setCampaignsExcludedCampaigns]: (state, payload) =>
    (state.campaignsExcludedCampaigns = payload),
  [Mutations.setCampaignsQueryBeforeChange]: (state, payload) =>
    (state.campaignsQueryBeforeChange = payload),
  [Mutations.setCampaignsStringForFilterBeforeChange]: (state, payload) =>
    (state.campaignsStringForFilterBeforeChange = payload)
};

export default {
  state,
  getters,
  mutations,
  actions
};
